import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { IDocumentResponseDTO } from '@app/core/model';
import { DocumentsContextService } from '@app/core/services/context/factory';
import { PlatformService } from '@app/core/services/context/platform.service';
import { ShellService } from '@mynexus/mfe-core';
import { FeatureToggleService, ProductNames } from '@mynexus/mynexus-ui-lib';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-edit-date-time',
    templateUrl: './edit-date-time.component.html',
    styleUrls: ['./edit-date-time.component.scss'],
    standalone: false
})
export class EditDateTimeComponent implements OnChanges {
  @Input() data: IDocumentResponseDTO;
  @Input() tenantId: string;
  @Input() showEdit = false; // Overarching control flag, set True on Documents-Tab, is False everywhere we DONT want to show the edit feature
  @Input() authorizationByProductId: string;
  @Output() editEvent = new EventEmitter<IDocumentResponseDTO>();

  // Model to bind the calendar picker to
  dateTimeFaxReceived: Date = null;
  minDate: Date = new Date('June 19, 2023'); // Minimum date, as set in US 181342
  maxDate: Date = new Date();

  // Flag to control the edit vs view display modes
  editing = false;
  oneDayCheck = new Date().getTime() - (24 * 60 * 60 * 1000); // used in edit checking below
  private readonly featureToggleService = inject(FeatureToggleService);
  private readonly destroyRef = inject(DestroyRef);
  private pacProductAndLobMigration = false;

  constructor(
    public contextService: PlatformService,
    private documentsContextService: DocumentsContextService,
    private ShellService: ShellService
  ) {
    this.featureToggleService.getFeatureAccess('PacProductAndLobMigration')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isEnabled) => {
        this.pacProductAndLobMigration = isEnabled;
      });
  }

  toggleEditing() {
    this.editing = !this.editing;
    this.dateTimeFaxReceived = this.editing ? this.dateTimeFaxReceived : new Date(this.data.dateTimeFaxReceived);
  }

  ngOnChanges (changes: SimpleChanges) {
    // Once we receive our input, acst to Date and assign to model variable
    if (changes['data']) {
      this.dateTimeFaxReceived = new Date(this.data.dateTimeFaxReceived);
      this.maxDate = new Date(this.data.createdOn);
    }
  }

  /*
    Edit is available under 2 rules:
    1. User is regular internal, and it's been less than 24 hours since document upload
    2. User is OpsAdmin, in which case they can ALWAYS edit

    Edit shall NOT occur:
    1. External users uploads should not be edited
    2. Letters/Outgoing faxes should not be edited
  */
  shouldShowEdit(): boolean {
    // Fix for any times that are given in UTC from streams.
    // Once we're all in UTC, we can remove this and simplify.
    const createdOn = this.data.createdOn.includes('Z') ? new Date(this.data.createdOn) : new Date(`${this.data.createdOn}Z`);
    const canDMEPOSEdit = this.ShellService.authenticationService.userGroups.some(x => x.toLowerCase() === 'DMEPOSMedicareOperationsAdmin'.toLowerCase()) && this.contextService.widgetContext === ProductNames.DMEPOS;
    const canHHEdit = this.ShellService.authenticationService.userGroups.some(x => x.toLowerCase() === 'HHOperationsAdmin'.toLowerCase()) && this.contextService.widgetContext === ProductNames.HomeHealth;

    // Major control, this is the tab/page dependent rendering flag
    if (!this.showEdit || !this.data) {
      return false;
    }
    else if (this.data.isExternal) {
      return false;
    }
    else if (this.data.documentSource !== 'Upload') {
      return false;
    }
    // 24Hour setup for Reg user, Ops admin anytime.
    else if (canDMEPOSEdit || canHHEdit || this.canPACEdit()) {
      return true;
    }
    else if (createdOn.getTime() >= this.oneDayCheck && this.data.createdByEmail === this.ShellService.authenticationService.userEmail) {
      return true;
    }

    // Default to not show control
    return false;
  }

  updateDocumentReceivedTime(): void {
    // Convert date object to string and assign it to the document to be updated.
    this.data.dateTimeFaxReceived = this.dateTimeFaxReceived.toISOString();

    // Make our update PUT call
    this.documentsContextService.updateDocumentReceivedTime(this.tenantId, this.authorizationByProductId, this.data).subscribe({
      next: res => {
        // Emit the updated document, so parent can refresh the table/row
        this.editEvent.emit(res);

        // Update complete, close edit mode
        this.toggleEditing();
      },
      error: () => {
        // Discard response, reset table.
      }
    });
  }

  // Note: PAC Commercial eligibility (tenant and state) is passed in as part of the document
  // permissions for editing date/time. As a result, we only need to check the user roles here
  private canPACEdit(): boolean {
    const context = this.contextService.widgetContext;
    const userGroups = this.ShellService.authenticationService.userGroups.map(x => x.toLowerCase());

    // Roles set as lowercase for comparison
    const hasEligibleMARole = userGroups.some(role => role === 'PACOperationsAdmin'.toLowerCase());
    const hasEligibleCommercialRole = userGroups.some(role => role === 'PACCommercialOperationsAdmin'.toLowerCase());
    
    const isPAC = context === ProductNames.PACIM;

    return this.pacProductAndLobMigration
      ? (hasEligibleMARole || hasEligibleCommercialRole) && isPAC
      : (
          (hasEligibleMARole && isPAC) ||
          (hasEligibleCommercialRole && context === ProductNames.PacCommercial)
        );
  }
}
